import { Box, Divider, Tab, TabList, TabPanel, TabPanels, Tabs, Text, useDisclosure } from '@chakra-ui/react';
import BrandKitImage from 'assets/images/brand_kit.png';
import { UPGRADE_REDIRECT_BIZ_LINK } from 'constants/links';
import { CLICK_ON_PAID_FEATURE, PALETTE_APPLIED, UPGRADE } from 'constants/mixpanel';
import { useVenngageE1Gate } from 'hooks/useVenngageE1Gate';
import { useChameleon } from 'libs/third-party/Chameleon';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { CommonGatedUpgradeModal } from 'modules/Editor/components/Modals/CommonGatedUpgradeModal';
import { ModalUpgradeTypeEnum } from 'modules/Editor/components/Modals/CommonGatedUpgradeModal.types';
import { applySelectedColorPalette, useAppDispatch } from 'modules/Editor/store';
import { UserTypes, useUser } from 'modules/User';
import { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { setAnalyticsUpgradeFeatureProp } from 'utils/sessionStorage';
import { NewWidget } from 'widgets/Widget.types';

import { BrandKitTab } from './BrandKitTab';
import { ExploreTab } from './ExploreTab/ExploreTab';

export interface BrandKitMenuProps {
  onItemClick: (widget: NewWidget | NewWidget[]) => void;
  disableDragDrop?: boolean;
}

export const BrandKitMenu = memo(({ onItemClick, disableDragDrop }: BrandKitMenuProps) => {
  // Upgrade modal for non-biz users when they try to use brand kit
  const { isOpen: isUpgradeModal, onOpen: onOpenUpgradeModal, onClose: onCloseUpgradeModal } = useDisclosure();
  const { trackChameleon } = useChameleon();
  const { isFreeUser, isPremiumUser, isGuestRole } = useUser();
  const { venngageE2 } = useVenngageE1Gate();

  const isMultiTabView = !!venngageE2?.experiments?.brandKitLeftPanelMultiTabView;

  // Free and Premium users should not be able to use but see the logo's with business badge labeled.
  const hasBrandKitPermission = !isFreeUser && !isPremiumUser && !isGuestRole;
  const dispatch = useAppDispatch();

  const handleShuffleColors = useCallback(
    (colors: string[], isBrandKit?: boolean) => {
      if (isBrandKit && !hasBrandKitPermission) {
        onOpenUpgradeModal();
        Mixpanel.track(CLICK_ON_PAID_FEATURE.eventName, {
          feature: UPGRADE.feature.BrandkitPalette,
          plan: UserTypes.Business,
        });
        return;
      }

      dispatch(applySelectedColorPalette({ colors }));
      Mixpanel.track(PALETTE_APPLIED.eventName, {
        type: isBrandKit ? PALETTE_APPLIED.type.BrandKit : PALETTE_APPLIED.type.Generic,
      });
    },
    [hasBrandKitPermission, dispatch, onOpenUpgradeModal],
  );

  const onClickUpgrade = () => {
    setAnalyticsUpgradeFeatureProp(UPGRADE.feature.BrandkitPalette);
    window.open(UPGRADE_REDIRECT_BIZ_LINK, '_self');
    Mixpanel.track(UPGRADE.eventName, {
      feature: UPGRADE.feature.BrandkitPalette,
      plan: UserTypes.Business,
    });
    trackChameleon(UPGRADE.eventName);
  };

  return (
    <Box h='full'>
      <CommonGatedUpgradeModal
        isOpen={isUpgradeModal}
        onClose={onCloseUpgradeModal}
        onClickUpgrade={onClickUpgrade}
        upgradePlan={UserTypes.Business}
        modalImg={BrandKitImage}
        modalType={ModalUpgradeTypeEnum.BrandKit}
      />
      {isMultiTabView ? (
        <MultiTabView
          onItemClick={onItemClick}
          disableDragDrop={disableDragDrop}
          handleShuffleColors={handleShuffleColors}
        />
      ) : (
        <SingleTabView
          onItemClick={onItemClick}
          disableDragDrop={disableDragDrop}
          handleShuffleColors={handleShuffleColors}
        />
      )}
    </Box>
  );
});
BrandKitMenu.displayName = 'BrandTab';

// TODO after experiment is complete, clean this up - should only keep one
interface BrandKitViewProps {
  onItemClick: (widget: NewWidget | NewWidget[]) => void;
  disableDragDrop?: boolean;
  handleShuffleColors: (colors: string[], isBrandKit?: boolean) => void;
}
const MultiTabView = ({ onItemClick, disableDragDrop, handleShuffleColors }: BrandKitViewProps) => {
  const { t } = useTranslation('editor_widget_menu', { useSuspense: false, keyPrefix: 'sideMenu.brandKit' });

  return (
    <Tabs variant='left-menu' isLazy>
      <TabList h='3rem'>
        <Tab>{t('tabs.assets')}</Tab>
        <Tab>{t('tabs.explore')}</Tab>
      </TabList>
      <TabPanels overflowY={'auto'} height={'calc(100vh - 10rem)'}>
        <TabPanel p='0'>
          <BrandKitTab
            onLogoClick={onItemClick}
            disableDragDrop={disableDragDrop}
            handleShuffleColors={handleShuffleColors}
            isScrollable
          />
        </TabPanel>
        <TabPanel h='full' w='full' paddingTop='4'>
          <ExploreTab handleShuffleColors={handleShuffleColors} />
        </TabPanel>
      </TabPanels>
    </Tabs>
  );
};

const SingleTabView = ({ onItemClick, disableDragDrop, handleShuffleColors }: BrandKitViewProps) => {
  const { t } = useTranslation('editor_widget_menu', { useSuspense: false, keyPrefix: 'sideMenu.brandKit' });

  return (
    <Box h='full' overflow='auto'>
      <Box>
        <BrandKitTab
          onLogoClick={onItemClick}
          disableDragDrop={disableDragDrop}
          handleShuffleColors={handleShuffleColors}
          autoBrandView='card'
        />
      </Box>
      <Divider />
      <Box pt={4}>
        <Text fontSize='sm' fontWeight='semibold' pb={2} px={4}>
          {t('explorePalettes')}
        </Text>
        <ExploreTab handleShuffleColors={handleShuffleColors} />
      </Box>
    </Box>
  );
};
